import { FastColor } from '@ant-design/fast-color';
import { MapToken, SeedToken } from 'antd/es/theme/interface';

const SEED_COLORS: (keyof SeedToken)[] = [
  'colorInfo',
  'colorPrimary',
  'colorSuccess',
  'colorWarning',
  'colorError',
];

const DARK_PALETTE_TONES: {
  saturation: number;
  brightness: number;
  hue: number;
  amount: number;
}[] = [
  { saturation: 100, brightness: 35, hue: 0, amount: 10 }, // the darkest
  { saturation: 100, brightness: 43, hue: 0, amount: 8 },
  { saturation: 100, brightness: 51, hue: 0, amount: 6 },
  { saturation: 90, brightness: 59, hue: 0, amount: 4 },
  { saturation: 77, brightness: 67, hue: 0, amount: 2 },
  { saturation: 0, brightness: 0, hue: 0, amount: 0 }, // original
  { saturation: 52, brightness: 84, hue: 0, amount: 0 },
  { saturation: 46, brightness: 88, hue: 0, amount: 0 },
  { saturation: 40, brightness: 92, hue: 0, amount: 0 },
  { saturation: 27, brightness: 100, hue: 0, amount: 0 }, // the lightest
];

function normalizeHue(value: number): number {
  let hue = value;

  if (hue < 0) {
    hue += 360;
  } else if (hue >= 360) {
    hue -= 360;
  }

  return hue;
}

function getPercentage(value: number): number {
  if (value > 100) {
    return 1;
  } else if (value <= 0) {
    return 0;
  }

  return value / 100;
}

export function generateDarkColorPalette({
  color,
  backgroundColor,
}: {
  color: string;
  backgroundColor: string;
}): string[] {
  const baseColor = new FastColor(color);
  const baseHsv = baseColor.toHsv();

  return DARK_PALETTE_TONES.map(({ saturation, brightness, hue, amount }, index) => {
    // original color
    if (index === 5) {
      return color;
    }

    const tintedColor = new FastColor({
      h: normalizeHue(baseHsv.h + hue),
      s: getPercentage(saturation),
      v: getPercentage(brightness),
    });

    return tintedColor.mix(backgroundColor, amount).toHexString();
  });
}

function populateMapColors(seedColorKey: keyof SeedToken, colorPalette: string[]) {
  return {
    [seedColorKey + 'Bg']: colorPalette[0],
    [seedColorKey + 'BgHover']: colorPalette[1],
    [seedColorKey + 'Border']: colorPalette[2],
    [seedColorKey + 'BorderHover']: colorPalette[3],
    [seedColorKey + 'Hover']: colorPalette[4],
    [seedColorKey]: colorPalette[5],
    [seedColorKey + 'Active']: colorPalette[6],
    [seedColorKey + 'TextHover']: colorPalette[7],
    [seedColorKey + 'Text']: colorPalette[8],
    [seedColorKey + 'TextActive']: colorPalette[9],
  };
}

export const generateDarkMapTokens = ({ colorBgLayout, ...seedTokens }: MapToken) => {
  const mappedTokens = SEED_COLORS.map((tokenKey) => {
    const colorPalette = generateDarkColorPalette({
      color: seedTokens[tokenKey] as string,
      backgroundColor: colorBgLayout,
    });

    return populateMapColors(tokenKey, colorPalette);
  });

  return Object.assign({}, ...mappedTokens);
};
